import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { GOOGLE_REVIEW_URL } from "../lib/brand";
import { CheckCircle2, Loader2, XCircle, Flame, Star } from "lucide-react";

const BalanceSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", booking: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", booking: null }); return; }
    let active = true;
    const poll = async () => {
      try {
        const r = await api.get(`/bookings/balance/status/${sessionId}`);
        if (!active) return;
        if (r.data.payment_status === "paid") {
          setState({ status: "paid", booking: r.data.booking });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState({ status: "timeout", booking: r.data.booking });
          return;
        }
        setTimeout(poll, 2500);
      } catch {
        if (!active) return;
        setState({ status: "error", booking: null });
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-[80vh] flex items-center" data-testid="balance-success-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 w-full">
        <div className="border border-[#262626] bg-[#0a0a0a] p-10 md:p-14">
          {state.status === "polling" && (
            <>
              <Loader2 size={48} className="text-[#D90429] animate-spin" />
              <h1 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Confirming payment…</h1>
              <p className="mt-3 text-[#A3A3A3]">Hold tight, we're verifying with Stripe.</p>
            </>
          )}
          {state.status === "paid" && (
            <>
              <CheckCircle2 size={48} className="text-[#10B981]" />
              <div className="font-display uppercase tracking-[0.25em] text-xs text-[#10B981] mt-6">Payment Received</div>
              <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tighter">
                Thanks! <span className="text-[#D90429]">Paid in full.</span>
              </h1>
              <p className="mt-4 text-[#A3A3A3]">Your balance has been received. Receipt sent to your email.</p>
              {state.booking && (
                <div className="mt-8 border border-[#262626] divide-y divide-[#1a1a1a]">
                  <Row label="Service" value={state.booking.service_name} />
                  <Row label="Total Paid" value={`$${state.booking.amount.toFixed(2)}`} highlight />
                </div>
              )}

              {/* Google Review CTA — most important post-payment ask */}
              <div className="mt-8 border-2 border-[#D90429] bg-[#D90429]/10 p-6 md:p-8 text-center" data-testid="review-cta">
                <div className="flex justify-center mb-3">
                  {[0,1,2,3,4].map(i => <Star key={i} size={22} className="text-[#D90429] fill-[#D90429]"/>)}
                </div>
                <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">Loved the service?</div>
                <h2 className="mt-2 font-display text-2xl md:text-3xl font-black uppercase tracking-tight">
                  Help a small business — leave a quick review
                </h2>
                <p className="mt-3 text-sm text-[#D4D4D4]">Takes 30 seconds. Your honest review helps your neighbors find us.</p>
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="review-cta-btn"
                  className="mt-5 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-7 py-4 text-sm transition-colors"
                >
                  <Star size={16} className="fill-white"/> Leave a 5-Star Review
                </a>
              </div>

              {state.booking && <TipSection booking={state.booking} />}
              <Link to="/" data-testid="balance-success-home" className="mt-10 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-8 py-4 transition-colors">
                <Flame size={16}/> Back to home
              </Link>
            </>
          )}
          {(state.status === "timeout" || state.status === "error") && (
            <>
              <XCircle size={48} className="text-[#EF4444]" />
              <h1 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Payment not confirmed</h1>
              <p className="mt-3 text-[#A3A3A3]">If you completed payment, give it a minute and refresh. Otherwise, please try again.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight }) => (
  <div className="flex justify-between px-5 py-3">
    <span className="text-[#A3A3A3] text-sm">{label}</span>
    <span className={`text-sm ${highlight ? "text-[#10B981] font-bold text-lg" : "text-white"}`}>{value}</span>
  </div>
);

const TipSection = ({ booking }) => {
  const [selected, setSelected] = useState(null); // percent number or "custom"
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  const total = booking.amount || 0;
  const tipAmount =
    selected === "custom"
      ? parseFloat(custom) || 0
      : selected
      ? Math.round(total * (selected / 100) * 100) / 100
      : 0;

  const sendTip = async () => {
    if (tipAmount < 1) return;
    setLoading(true);
    try {
      const r = await api.post(`/bookings/${booking.id}/tip`, { amount: tipAmount });
      window.location.href = r.data.url;
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <div className="mt-10 border border-[#D90429]/40 bg-[#D90429]/5 p-6" data-testid="tip-section">
      <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-2">Loved the service?</div>
      <div className="font-display text-2xl font-bold uppercase tracking-tight mb-4">Add a tip 💸</div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[15, 20, 25].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => { setSelected(p); setCustom(""); }}
            data-testid={`tip-${p}`}
            className={`p-3 border transition-colors text-left ${selected === p ? "border-[#D90429] bg-[#D90429]/15" : "border-[#262626] hover:border-[#D90429]/60"}`}
          >
            <div className="font-display font-bold uppercase tracking-wider text-sm">{p}%</div>
            <div className="text-xs text-[#A3A3A3] mt-1">${(Math.round(total * p / 100 * 100) / 100).toFixed(2)}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2 mb-4">
        <button
          type="button"
          onClick={() => setSelected("custom")}
          data-testid="tip-custom"
          className={`p-3 border transition-colors text-left ${selected === "custom" ? "border-[#D90429] bg-[#D90429]/15" : "border-[#262626] hover:border-[#D90429]/60"}`}
        >
          <div className="font-display font-bold uppercase tracking-wider text-sm">Custom</div>
          <div className="text-xs text-[#A3A3A3] mt-1">Enter any amount</div>
        </button>
        <button
          type="button"
          onClick={() => { setSelected(null); setCustom(""); }}
          data-testid="tip-skip"
          className={`px-4 border transition-colors ${selected === null ? "border-[#A3A3A3] bg-white/5" : "border-[#262626] hover:border-[#A3A3A3]"} font-display uppercase tracking-wider text-xs`}
        >
          No thanks
        </button>
      </div>
      {selected === "custom" && (
        <input
          type="number"
          min="1"
          step="1"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Amount in $"
          data-testid="tip-custom-amount"
          className="w-full h-11 bg-[#050505] border border-[#262626] focus:border-[#D90429] outline-none px-4 text-white mb-3"
        />
      )}
      {selected !== null && tipAmount > 0 && (
        <button
          onClick={sendTip}
          disabled={loading}
          data-testid="tip-pay-btn"
          className="w-full bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-6 py-3 inline-flex items-center justify-center gap-2 transition-colors"
        >
          {loading ? "Redirecting…" : `Send $${tipAmount.toFixed(2)} Tip`}
        </button>
      )}
    </div>
  );
};

export default BalanceSuccessPage;
